import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { AddressContext } from '../api/types'
import { usePermessiModuli } from '../auth/permessi'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner, formatDateTime } from './common'

/**
 * La testa della scheda di un **indirizzo aggiuntivo** (VIP, IP d'interfaccia,
 * alias): prima di tutto **di chi è** — il CI che lo espone, con il link — poi la
 * descrizione (funzione, note, nome DNS) modificabile sul posto, il nome dal DNS
 * inverso visto dalle scansioni e, se c'è phpIPAM, che cosa lo documenta.
 *
 * Senza, la scheda di un indirizzo mostrava solo l'IP: la domanda con cui la si
 * apre — «questo cos'è, di chi è?» — richiedeva di andare a cercare nelle
 * relazioni. Vedi `GET /cis/{id}/address-context`.
 */
export function AnagraficaIndirizzo({ ciId, haIpam }: { ciId: string; haIpam: boolean }) {
  const contesto = useAsync(() => api.ciAddressContext(ciId), [ciId])
  const d = contesto.data
  if (contesto.loading && !d) return <Spinner />
  if (!d?.is_address) return null
  return (
    <>
      <Proprietari d={d} />
      <Descrizione d={d} onSalvato={() => void contesto.reload()} />
      {haIpam && d.address && <IpamIndirizzo ciId={ciId} ip={d.address} />}
    </>
  )
}

function Proprietari({ d }: { d: AddressContext }) {
  const owners = d.owners ?? []
  return (
    <Card title={t('Di chi è questo indirizzo')}>
      {owners.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {t('Nessun CI espone più questo indirizzo: il legame è stato tolto o l’oggetto che lo portava non c’è più.')}
        </p>
      ) : (
        <dl className="kv">
          {owners.map((o) => (
            <div key={o.id} style={{ display: 'contents' }}>
              <dt>{t('Proprietario')}</dt>
              <dd>
                <Link to={`/cis/${o.id}`}><strong>{o.name}</strong></Link>
                <span className="muted">
                  {' · '}{o.ci_class ?? o.ci_type}
                  {o.vendor ? ` · ${o.vendor}` : ''}
                  {o.mgmt_ip ? ` · ${o.mgmt_ip}` : ''}
                </span>
                <div className="field__hint" style={{ margin: 0 }}>
                  {t('Legato dal {da}, ultima conferma {quando}', {
                    da: o.since ? formatDateTime(o.since) : '—',
                    quando: o.last_seen ? formatDateTime(o.last_seen) : '—',
                  })}
                </div>
              </dd>
            </div>
          ))}
        </dl>
      )}
      <p className="field__hint">
        {d.declared
          ? t('Indirizzo dichiarato a mano sulla scheda del proprietario.')
          : t('Indirizzo trovato dalla scansione fra quelli dell’apparato: chi lo raggiunge o ci fa traffico viene attribuito al proprietario.')}
      </p>
    </Card>
  )
}

function Descrizione({ d, onSalvato }: { d: AddressContext; onSalvato: () => void }) {
  const permessi = usePermessiModuli()
  const owner = (d.owners ?? [])[0]
  // Si scrive sul legame del proprietario: è lì che la descrizione vive, e da lì
  // la vede anche la scheda del proprietario.
  const puoModificare = permessi.scrive('cis') && Boolean(owner) && Boolean(d.address)
  const [modifica, setModifica] = useState(false)
  const [funzione, setFunzione] = useState(d.function ?? '')
  const [note, setNote] = useState(d.note ?? '')
  const [dns, setDns] = useState(d.dns_name ?? '')
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)

  async function salva() {
    if (!owner || !d.address) return
    setErrore(null)
    setLavoro(true)
    try {
      await api.addCiAddress(owner.id, { address: d.address, fqdn: dns.trim(), function: funzione.trim(), note: note.trim() })
      setModifica(false)
      onSalvato()
    } catch (err) {
      setErrore(err)
    } finally {
      setLavoro(false)
    }
  }

  return (
    <Card
      title={t('Descrizione')}
      actions={puoModificare && !modifica ? (
        <button type="button" className="secondary" onClick={() => setModifica(true)}>{t('Modifica')}</button>
      ) : undefined}
    >
      <ErrorBanner error={errore} />
      {modifica ? (
        <div className="form-grid">
          <div className="field">
            <label htmlFor="addr-funzione">{t('Funzione')}</label>
            <input id="addr-funzione" value={funzione} onChange={(e) => setFunzione(e.target.value)}
                   placeholder={t('gateway VLAN 31, VIP del bilanciatore…')} />
          </div>
          <div className="field">
            <label htmlFor="addr-dns">{t('Nome DNS')}</label>
            <input id="addr-dns" value={dns} onChange={(e) => setDns(e.target.value)} placeholder={t('nome.dominio')} />
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="addr-note">{t('Note')}</label>
            <input id="addr-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="rowactions" style={{ textAlign: 'left' }}>
            <button type="button" onClick={() => void salva()} disabled={lavoro}>{lavoro ? t('Salvataggio…') : t('Salva')}</button>
            <button type="button" className="secondary" onClick={() => setModifica(false)} disabled={lavoro}>{t('Annulla')}</button>
          </div>
        </div>
      ) : (
        <dl className="kv">
          <dt>{t('Funzione')}</dt>
          <dd>{d.function || <span className="muted">{t('non indicata')}</span>}</dd>
          <dt>{t('Note')}</dt>
          <dd>{d.note || <span className="muted">—</span>}</dd>
          <dt>{t('Nome DNS')}</dt>
          <dd className="mono">{d.dns_name || <span className="muted">—</span>}</dd>
          {d.vrf && (
            <>
              <dt>{t('VRF/VPN-INSTANCE')}</dt>
              <dd className="mono">{d.vrf}</dd>
            </>
          )}
          {d.reverse_dns && (
            <>
              <dt>{t('Nome dal DNS inverso')}</dt>
              <dd className="mono">{d.reverse_dns}</dd>
            </>
          )}
        </dl>
      )}
    </Card>
  )
}

function IpamIndirizzo({ ciId, ip }: { ciId: string; ip: string }) {
  const dati = useAsync(() => api.ciIpam(ciId), [ciId])
  const r = dati.data
  const voce = (r?.addresses ?? []).find((a) => a.ip === ip)
  return (
    <Card title={t('Cosa dice phpIPAM')}>
      {dati.loading && !r ? (
        <Spinner />
      ) : !r?.available ? (
        <p className="muted" style={{ margin: 0 }}>{r?.reason ?? t('phpIPAM non interrogabile.')}</p>
      ) : !voce ? (
        <p className="muted" style={{ margin: 0 }}>{t('phpIPAM non documenta questo indirizzo: è in uso ma non registrato.')}</p>
      ) : (
        <dl className="kv">
          <dt>{t('Descrizione')}</dt>
          <dd>{voce.description || '—'}</dd>
          <dt>{t('Nome host')}</dt>
          <dd className="mono">{voce.hostname || '—'}</dd>
          <dt>{t('Proprietario in IPAM')}</dt>
          <dd>{voce.owner || '—'}</dd>
          {voce.mac && (
            <>
              <dt>MAC</dt>
              <dd className="mono">{voce.mac}</dd>
            </>
          )}
        </dl>
      )}
    </Card>
  )
}
